import { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { LocationAutocomplete } from '../components/LocationAutocomplete';
import {
  User,
  MapPin,
  Shield,
  Bell,
  LogOut,
  Loader2,
  CheckCircle,
  Camera,
  Edit2,
  Save,
  X,
  Crown,
  Star,
  MessageSquare,
  AlertCircle,
  Settings,
  Lock,
  Mail,
  Smartphone,
  ChevronRight,
  Plus,
  Sliders,
  Trash2,
  CreditCard,
  Receipt,
} from 'lucide-react';
import CategoryPreferencePicker from '../components/CategoryPreferencePicker';
import { PaymentMethodsSection } from '../components/PaymentMethodsSection';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { toast } from '../lib/toast';
import { TrustBadge, TrustScoreCard, ReviewSummary } from '../components/TrustScoreDisplay';
import { ReviewCard } from '../components/ReviewComponents';

const MENU_ITEMS = [
  { id: 'profile', label: 'My Profile', icon: User },
  { id: 'notifications', label: 'Notifications', icon: Bell },
  { id: 'feed-preferences', label: 'Feed Preferences', icon: Sliders },
  { id: 'premium', label: 'WANTD+', icon: Crown },
  { id: 'payment-methods', label: 'Payment Methods', icon: CreditCard },
  { id: 'billing', label: 'Billing & invoices', icon: Receipt, route: '/account/billing' },
  { id: 'account', label: 'Account', icon: Settings },
  { id: 'signout', label: 'Sign Out', icon: LogOut, danger: true },
];

export default function ProfilePage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, logout, api, refreshUser } = useAuth();
  const fileInputRef = useRef(null);
  
  // Active section from URL or default
  const [activeSection, setActiveSection] = useState(() => {
    const section = searchParams.get('section');
    return MENU_ITEMS.find(m => m.id === section)?.id || 'profile';
  });
  
  // Profile states
  const [updating, setUpdating] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [editingProfile, setEditingProfile] = useState(false);
  const [editingLocation, setEditingLocation] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteEmailInput, setDeleteEmailInput] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  
  // Trust score and reviews state
  const [trustBreakdown, setTrustBreakdown] = useState(null);
  const [reviewSummary, setReviewSummary] = useState(null);
  const [recentReviews, setRecentReviews] = useState([]);
  const [loadingTrust, setLoadingTrust] = useState(true);
  
  // Form states
  const [firstName, setFirstName] = useState(user?.first_name || '');
  const [lastName, setLastName] = useState(user?.last_name || '');
  const [username, setUsername] = useState(user?.username || '');
  const [locationName, setLocationName] = useState(user?.location_name || '');
  const [locationCoords, setLocationCoords] = useState(user?.location || null);
  
  // Notification preferences — load saved values from user data, default to true
  const [notifications, setNotifications] = useState({
    matches: user?.notification_preferences?.matches !== false,
    messages: user?.notification_preferences?.messages !== false,
    weekly_reminder: user?.notification_preferences?.weekly_reminder !== false,
    saved_items: user?.notification_preferences?.saved_items !== false,
  });

  // Feed preference state — initialised from stored user preferences
  const [feedPrefCategories, setFeedPrefCategories] = useState(
    user?.preferred_categories ?? []
  );
  const [feedPrefSubcategories, setFeedPrefSubcategories] = useState(
    user?.preferred_subcategories ?? []
  );
  const [savingFeedPrefs, setSavingFeedPrefs] = useState(false);

  // Keep local state in sync if user context updates (e.g. after refreshUser or multi-tab)
  useEffect(() => {
    if (user?.preferred_categories) setFeedPrefCategories(user.preferred_categories);
    if (user?.preferred_subcategories) setFeedPrefSubcategories(user.preferred_subcategories);
  }, [user?.preferred_categories, user?.preferred_subcategories]);

  // Update URL when section changes
  useEffect(() => {
    if (activeSection !== 'profile') {
      setSearchParams({ section: activeSection });
    } else {
      setSearchParams({});
    }
  }, [activeSection, setSearchParams]);

  // Fetch trust score and reviews
  useEffect(() => {
    const fetchTrustData = async () => {
      if (!user?.id) return;
      
      setLoadingTrust(true);
      try {
        const [trustRes, summaryRes, reviewsRes] = await Promise.all([
          api.get(`/reviews/user/${user.id}/trust-score`).catch(() => null),
          api.get(`/reviews/user/${user.id}/summary`).catch(() => null),
          api.get(`/reviews/user/${user.id}?limit=5`).catch(() => null),
        ]);
        
        if (trustRes?.data) setTrustBreakdown(trustRes.data);
        if (summaryRes?.data) setReviewSummary(summaryRes.data);
        if (reviewsRes?.data) setRecentReviews(reviewsRes.data);
      } catch (error) {
        console.error('Failed to fetch trust data:', error);
      } finally {
        setLoadingTrust(false);
      }
    };
    
    fetchTrustData();
  }, [user?.id, api]);

  // Handle profile image upload
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post('/auth/profile/image', formData);
      
      if (response.data?.image_url) {
        toast.success('Profile photo updated!');
        await refreshUser();
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  // Handle save profile
  const handleSaveProfile = async () => {
    setUpdating(true);
    try {
      await api.put('/auth/profile/details', null, {
        params: {
          first_name: firstName || undefined,
          last_name: lastName || undefined,
          username: username !== user?.username ? username : undefined,
        }
      });
      toast.success('Profile updated!');
      setEditingProfile(false);
      await refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setUpdating(false);
    }
  };

  // Handle location select
  const handleLocationSelect = (place) => {
    setLocationName(place.name);
    setLocationCoords({ lat: place.lat, lng: place.lng });
  };

  // Handle save location
  const handleSaveLocation = async () => {
    if (!locationName || !locationCoords) {
      toast.error('Please select a location from the suggestions');
      return;
    }
    
    setUpdating(true);
    try {
      await api.put('/auth/profile/details', null, {
        params: { 
          location_name: locationName,
          location_lat: locationCoords.lat,
          location_lng: locationCoords.lng
        }
      });
      toast.success('Location updated!');
      setEditingLocation(false);
      await refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update location');
    } finally {
      setUpdating(false);
    }
  };

  // Handle notification toggle — persists to backend
  const handleNotificationToggle = async (key, value) => {
    setNotifications(prev => ({ ...prev, [key]: value }));
    try {
      await api.put('/auth/profile/notification-preferences', { [key]: value });
      toast.success('Notification preference updated');
    } catch (err) {
      // Revert on failure
      setNotifications(prev => ({ ...prev, [key]: !value }));
      toast.error('Failed to update preference');
    }
  };

  // Handle menu click
  const handleMenuClick = (item) => {
    if (item.id === 'signout') {
      logout();
      navigate('/');
    } else if (item.route) {
      navigate(item.route);
    } else {
      setActiveSection(item.id);
    }
  };

  // Render My Profile Section
  const renderProfileSection = () => (
    <div className="space-y-6">
      {/* Profile Header Card */}
      <div className="p-6 rounded-2xl bg-[var(--theme-card)] border border-[var(--theme-border)] shadow-card">
        <div className="flex items-start gap-6">
          {/* Avatar */}
          <div className="relative group">
            <div className="w-24 h-24 rounded-full overflow-hidden bg-[var(--theme-hover)] border-2 border-[var(--theme-border)]">
              {user?.profile_image ? (
                <img 
                  src={user.profile_image} 
                  alt={user.username}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#1E3A5F] to-[#C9956C]">
                  <span className="text-3xl font-bold text-white">
                    {user?.username?.charAt(0)?.toUpperCase() || 'U'}
                  </span>
                </div>
              )}
            </div>
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingImage}
              className="absolute bottom-0 right-0 w-8 h-8 rounded-full bg-[#1E3A5F] text-white flex items-center justify-center shadow-lg hover:bg-[#2a4a6f] transition-colors"
            >
              {uploadingImage ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Camera className="w-4 h-4" />
              )}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden"
            />
          </div>

          {/* Profile Info */}
          <div className="flex-1">
            {editingProfile ? (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    placeholder="First name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="bg-[var(--theme-bg-tertiary)] border-[var(--theme-border)]"
                  />
                  <Input
                    placeholder="Last name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="bg-[var(--theme-bg-tertiary)] border-[var(--theme-border)]"
                  />
                </div>
                <Input
                  placeholder="Username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="bg-[var(--theme-bg-tertiary)] border-[var(--theme-border)]"
                />
                <div className="flex gap-2">
                  <Button 
                    size="sm" 
                    onClick={handleSaveProfile}
                    disabled={updating}
                    className="bg-[#059669] hover:bg-[#047857] rounded-full"
                  >
                    {updating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                    Save
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => setEditingProfile(false)}
                    className="rounded-full border-[var(--theme-border)]"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2">
                  <h2 className="text-xl font-bold text-[var(--theme-text-primary)]">
                    {user?.first_name || user?.last_name 
                      ? `${user.first_name || ''} ${user.last_name || ''}`.trim()
                      : `@${user?.username}`
                    }
                  </h2>
                  <button 
                    onClick={() => setEditingProfile(true)}
                    className="p-1 hover:bg-[var(--theme-hover)] rounded transition-colors"
                  >
                    <Edit2 className="w-4 h-4 text-[var(--theme-text-secondary)]" />
                  </button>
                </div>
                {(user?.first_name || user?.last_name) && (
                  <p className="text-[var(--theme-text-secondary)]">@{user?.username}</p>
                )}
                <p className="text-sm text-[var(--theme-text-muted)]">{user?.email}</p>
                <div className="flex items-center gap-2 mt-2">
                  <TrustBadge 
                    tier={trustBreakdown?.trust_tier || 'new'} 
                    score={trustBreakdown?.trust_score || 0} 
                    size="md"
                  />
                  {user?.is_premium && (
                    <span className="px-3 py-1 rounded-full bg-[#C9956C] text-white text-xs font-bold uppercase flex items-center gap-1">
                      <Crown className="w-3 h-3" />
                      Premium
                    </span>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Location */}
      <div className="p-6 rounded-2xl bg-[var(--theme-card)] border border-[var(--theme-border)] shadow-card">
        <div className="flex items-center gap-3 mb-4">
          <MapPin className="w-5 h-5 text-[#C9956C]" />
          <h3 className="font-bold text-[var(--theme-text-primary)]">Location</h3>
        </div>
        
        {editingLocation ? (
          <div className="space-y-3">
            <LocationAutocomplete
              value={locationName}
              onChange={setLocationName}
              onSelect={handleLocationSelect}
              placeholder="Search suburb or postcode..."
            />
            <p className="text-xs text-[var(--theme-text-muted)]">
              Start typing and select from the suggestions
            </p>
            <div className="flex gap-2">
              <Button 
                size="sm" 
                onClick={handleSaveLocation}
                disabled={updating || !locationCoords}
                className="bg-[#059669] hover:bg-[#047857] rounded-full"
              >
                {updating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                Save
              </Button>
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => setEditingLocation(false)}
                className="rounded-full border-[var(--theme-border)]"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between">
              <p className="text-[var(--theme-text-primary)]">
                {user?.location_name || 'No location set'}
              </p>
              <Button 
                variant="outline" 
                size="sm"
                className="rounded-full border-[var(--theme-border)]"
                onClick={() => setEditingLocation(true)}
              >
                <Edit2 className="w-4 h-4 mr-2" />
                Edit
              </Button>
            </div>
            <p className="text-xs text-[var(--theme-text-secondary)] mt-2">
              Your exact location is never shared. Only used to find nearby matches.
            </p>
          </>
        )}
      </div>

      {/* Trust Score */}
      <div className="p-6 rounded-2xl bg-[var(--theme-card)] border border-[var(--theme-border)] shadow-card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Shield className="w-5 h-5 text-[#1E3A5F] dark:text-[#C9956C]" />
            <h3 className="font-bold text-[var(--theme-text-primary)]">Trust Score & Reviews</h3>
          </div>
          {loadingTrust && <Loader2 className="w-4 h-4 animate-spin text-[var(--theme-text-muted)]" />}
        </div>
        
        {trustBreakdown ? (
          <TrustScoreCard breakdown={trustBreakdown} summary={reviewSummary} />
        ) : (
          <div className="flex items-center gap-4">
            <div className="text-4xl font-black text-[var(--theme-text-primary)]">{user?.trust_score || 0}</div>
            <div className="flex-1">
              <div className="h-2 bg-[var(--theme-border)] rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-[#1E3A5F] to-[#C9956C] rounded-full transition-all duration-500"
                  style={{ width: `${Math.min((user?.trust_score || 0) / 100 * 100, 100)}%` }}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Review Summary */}
      {reviewSummary && reviewSummary.total_reviews > 0 && (
        <div className="p-6 rounded-2xl bg-[var(--theme-card)] border border-[var(--theme-border)] shadow-card">
          <div className="flex items-center gap-3 mb-4">
            <Star className="w-5 h-5 text-[#F59E0B]" />
            <h3 className="font-bold text-[var(--theme-text-primary)]">Review Summary</h3>
          </div>
          <ReviewSummary summary={reviewSummary} />
        </div>
      )}

      {/* Recent Reviews */}
      {recentReviews.length > 0 && (
        <div className="p-6 rounded-2xl bg-[var(--theme-card)] border border-[var(--theme-border)] shadow-card">
          <div className="flex items-center gap-3 mb-4">
            <MessageSquare className="w-5 h-5 text-[#1E3A5F]" />
            <h3 className="font-bold text-[var(--theme-text-primary)]">Recent Reviews</h3>
          </div>
          <div className="space-y-3">
            {recentReviews.map((review) => (
              <ReviewCard key={review.id} review={review} />
            ))}
          </div>
        </div>
      )}
    </div>
  );

  // Render Notifications Section
  const renderNotificationsSection = () => (
    <div className="space-y-6">
      <div className="p-6 rounded-2xl bg-[var(--theme-card)] border border-[var(--theme-border)] shadow-card">
        <div className="flex items-center gap-3 mb-6">
          <Bell className="w-5 h-5 text-orange-500" />
          <h3 className="font-bold text-[var(--theme-text-primary)]">Notification Preferences</h3>
        </div>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 rounded-xl bg-[var(--theme-hover)]">
            <div>
              <p className="font-medium text-[var(--theme-text-primary)]">New Matches</p>
              <p className="text-sm text-[var(--theme-text-secondary)]">
                Get notified when your items match with buyers
              </p>
            </div>
            <Switch
              checked={notifications.matches}
              onCheckedChange={(val) => handleNotificationToggle('matches', val)}
            />
          </div>
          
          <div className="flex items-center justify-between p-4 rounded-xl bg-[var(--theme-hover)]">
            <div>
              <p className="font-medium text-[var(--theme-text-primary)]">Messages</p>
              <p className="text-sm text-[var(--theme-text-secondary)]">
                Receive notifications for new messages and offers
              </p>
            </div>
            <Switch
              checked={notifications.messages}
              onCheckedChange={(val) => handleNotificationToggle('messages', val)}
            />
          </div>
          
          <div className="flex items-center justify-between p-4 rounded-xl bg-[var(--theme-hover)]">
            <div>
              <p className="font-medium text-[var(--theme-text-primary)]">Weekly Digest</p>
              <p className="text-sm text-[var(--theme-text-secondary)]">
                Weekly summary of your activity and recommendations
              </p>
            </div>
            <Switch
              checked={notifications.weekly_reminder}
              onCheckedChange={(val) => handleNotificationToggle('weekly_reminder', val)}
            />
          </div>

          <div className="flex items-center justify-between p-4 rounded-xl bg-[var(--theme-hover)]">
            <div>
              <p className="font-medium text-[var(--theme-text-primary)]">Saved Items Updates</p>
              <p className="text-sm text-[var(--theme-text-secondary)]">
                Reminders and activity alerts about items you've saved
              </p>
            </div>
            <Switch
              checked={notifications.saved_items}
              onCheckedChange={(val) => handleNotificationToggle('saved_items', val)}
            />
          </div>
        </div>
      </div>

      {/* Push Notifications */}
      <div className="p-6 rounded-2xl bg-[var(--theme-card)] border border-[var(--theme-border)] shadow-card">
        <div className="flex items-center gap-3 mb-4">
          <Smartphone className="w-5 h-5 text-[#1E3A5F]" />
          <h3 className="font-bold text-[var(--theme-text-primary)]">Push Notifications</h3>
        </div>
        
        <p className="text-sm text-[var(--theme-text-secondary)] mb-4">
          Enable browser push notifications to stay updated even when you're not on WANTD.
        </p>

        <Button
          variant="outline"
          className="w-full rounded-full border-[var(--theme-border)]"
          onClick={() => toast.info('Push notification setup coming soon')}
        >
          Enable Push Notifications
        </Button>
      </div>
    </div>
  );

  // Render WANTD+ Section
  const renderPremiumSection = () => (
    <div className="space-y-6">
      <div className="p-6 rounded-2xl bg-[var(--theme-card)] border border-[var(--theme-border)] shadow-card">
        <div className="flex items-center gap-3 mb-4">
          <Crown className="w-5 h-5 text-[#C9956C]" />
          <h3 className="font-bold text-[var(--theme-text-primary)]">WANTD+ Membership</h3>
        </div>

        {user?.is_premium ? (
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-4 rounded-xl bg-gradient-to-r from-[#C9956C]/10 to-[#C9956C]/5 border border-[#C9956C]/30">
              <Crown className="w-8 h-8 text-[#C9956C]" />
              <div>
                <p className="font-bold text-[var(--theme-text-primary)]">You're a WANTD+ Member!</p>
                <p className="text-sm text-[var(--theme-text-secondary)]">
                  Enjoying premium features and lower fees
                </p>
              </div>
            </div>
            
            <div className="space-y-2">
              <h4 className="font-semibold text-[var(--theme-text-primary)]">Your Benefits:</h4>
              <ul className="space-y-2 text-sm text-[var(--theme-text-secondary)]">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-[#059669]" />
                  AI-powered bulk listing
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-[#059669]" />
                  Unlimited listing slots
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-[#059669]" />
                  Priority matching
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-[#059669]" />
                  Premium badge on profile
                </li>
              </ul>
            </div>

            <Button
              variant="outline"
              className="w-full rounded-full border-[var(--theme-border)]"
              onClick={() => navigate('/premium')}
            >
              Manage Subscription
            </Button>
          </div>
        ) : user?.is_trial_active ? (
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-4 rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
              <Crown className="w-8 h-8 text-blue-600" />
              <div>
                <p className="font-bold text-[var(--theme-text-primary)]">Free Trial Active</p>
                <p className="text-sm text-[var(--theme-text-secondary)]">
                  Enjoying WANTD+ features for free
                </p>
              </div>
            </div>

            <Button
              className="w-full bg-[#C9956C] hover:bg-[#B8845B] text-white rounded-full"
              onClick={() => navigate('/premium')}
            >
              <Crown className="w-4 h-4 mr-2" />
              Upgrade to WANTD+
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-[var(--theme-text-secondary)]">
              Unlock premium features with WANTD+ membership.
            </p>
            
            <div className="space-y-2">
              <h4 className="font-semibold text-[var(--theme-text-primary)]">WANTD+ Benefits:</h4>
              <ul className="space-y-2 text-sm text-[var(--theme-text-secondary)]">
                <li className="flex items-center gap-2">
                  <Plus className="w-4 h-4 text-[#C9956C]" />
                  AI-powered bulk listing from photos
                </li>
                <li className="flex items-center gap-2">
                  <Plus className="w-4 h-4 text-[#C9956C]" />
                  Unlimited listing slots
                </li>
                <li className="flex items-center gap-2">
                  <Plus className="w-4 h-4 text-[#C9956C]" />
                  Priority in match results
                </li>
                <li className="flex items-center gap-2">
                  <Plus className="w-4 h-4 text-[#C9956C]" />
                  Premium profile badge
                </li>
              </ul>
            </div>

            <Button
              className="w-full bg-[#C9956C] hover:bg-[#B8845B] text-white rounded-full"
              onClick={() => navigate('/premium')}
            >
              <Crown className="w-4 h-4 mr-2" />
              Get WANTD+
            </Button>
          </div>
        )}
      </div>
    </div>
  );

  // Render Account Section
  const renderAccountSection = () => (
    <div className="space-y-6">
      {/* Security */}
      <div className="p-6 rounded-2xl bg-[var(--theme-card)] border border-[var(--theme-border)] shadow-card">
        <div className="flex items-center gap-3 mb-6">
          <Lock className="w-5 h-5 text-[#1E3A5F]" />
          <h3 className="font-bold text-[var(--theme-text-primary)]">Security</h3>
        </div>
        
        <div className="space-y-3">
          <button
            onClick={() => toast.info('Password reset email will be sent')}
            className="w-full flex items-center justify-between p-4 rounded-xl bg-[var(--theme-hover)] hover:bg-[var(--theme-border)]/50 transition-colors text-left"
          >
            <div className="flex items-center gap-3">
              <Lock className="w-5 h-5 text-[var(--theme-text-secondary)]" />
              <div>
                <p className="font-medium text-[var(--theme-text-primary)]">Change Password</p>
                <p className="text-sm text-[var(--theme-text-secondary)]">Update your account password</p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-[var(--theme-text-muted)]" />
          </button>

          <button
            onClick={() => toast.info('MFA setup coming soon')}
            className="w-full flex items-center justify-between p-4 rounded-xl bg-[var(--theme-hover)] hover:bg-[var(--theme-border)]/50 transition-colors text-left"
          >
            <div className="flex items-center gap-3">
              <Smartphone className="w-5 h-5 text-[var(--theme-text-secondary)]" />
              <div>
                <p className="font-medium text-[var(--theme-text-primary)]">Two-Factor Authentication</p>
                <p className="text-sm text-[var(--theme-text-secondary)]">Add an extra layer of security</p>
              </div>
            </div>
            <span className="text-xs px-2 py-1 rounded-full bg-[var(--theme-hover)] text-[var(--theme-text-muted)]">
              Coming Soon
            </span>
          </button>
        </div>
      </div>

      {/* Email */}
      <div className="p-6 rounded-2xl bg-[var(--theme-card)] border border-[var(--theme-border)] shadow-card">
        <div className="flex items-center gap-3 mb-6">
          <Mail className="w-5 h-5 text-[#C9956C]" />
          <h3 className="font-bold text-[var(--theme-text-primary)]">Email Settings</h3>
        </div>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 rounded-xl bg-[var(--theme-hover)]">
            <div>
              <p className="text-sm text-[var(--theme-text-secondary)]">Current Email</p>
              <p className="font-medium text-[var(--theme-text-primary)]">{user?.email}</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="rounded-full border-[var(--theme-border)]"
              onClick={() => toast.info('Email change coming soon')}
            >
              Change
            </Button>
          </div>
        </div>
      </div>

      {/* Danger Zone */}
      <div className="p-6 rounded-2xl bg-[var(--theme-card)] border border-red-200 dark:border-red-800 shadow-card">
        <div className="flex items-center gap-3 mb-4">
          <AlertCircle className="w-5 h-5 text-red-500" />
          <h3 className="font-bold text-red-600 dark:text-red-400">Danger Zone</h3>
        </div>

        <p className="text-sm text-[var(--theme-text-secondary)] mb-4">
          Permanently delete your account and all associated data including listings, messages, offers, and reviews. This cannot be undone.
        </p>

        {!showDeleteModal ? (
          <Button
            variant="outline"
            className="rounded-full border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
            onClick={() => { setShowDeleteModal(true); setDeleteEmailInput(''); }}
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete Account
          </Button>
        ) : (
          <div className="space-y-3 p-4 rounded-xl bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-700">
            <p className="text-sm font-medium text-red-700 dark:text-red-400">
              Type your email address to confirm deletion:
            </p>
            <p className="text-xs text-red-600 dark:text-red-500 font-mono">{user?.email}</p>
            <Input
              type="email"
              placeholder="your@email.com"
              value={deleteEmailInput}
              onChange={e => setDeleteEmailInput(e.target.value)}
              className="border-red-300 focus:ring-red-400 bg-white dark:bg-transparent"
              autoFocus
            />
            <div className="flex gap-2 flex-wrap">
              <Button
                variant="outline"
                size="sm"
                onClick={() => { setShowDeleteModal(false); setDeleteEmailInput(''); }}
                className="rounded-full"
              >
                Cancel
              </Button>
              <Button
                size="sm"
                disabled={deleteLoading || deleteEmailInput.trim().toLowerCase() !== user?.email?.toLowerCase()}
                onClick={async () => {
                  setDeleteLoading(true);
                  try {
                    await api.delete('/users/me', { data: { email_confirmation: deleteEmailInput.trim() } });
                    toast.success('Your account has been deleted.');
                    await logout();
                    navigate('/');
                  } catch (err) {
                    toast.error(err.response?.data?.detail || 'Deletion failed. Please try again.');
                    setDeleteLoading(false);
                  }
                }}
                className="rounded-full bg-red-600 hover:bg-red-700 text-white"
              >
                {deleteLoading
                  ? <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  : <Trash2 className="w-4 h-4 mr-2" />}
                Permanently Delete
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // Render Feed Preferences Section
  const renderFeedPreferencesSection = () => {
    const handleSaveFeedPrefs = async () => {
      setSavingFeedPrefs(true);
      try {
        await api.post('/users/preferences', {
          preferred_categories: feedPrefCategories,
          preferred_subcategories: feedPrefSubcategories,
        });
        toast.success('Feed preferences saved!');
        await refreshUser();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to save preferences');
      } finally {
        setSavingFeedPrefs(false);
      }
    };

    // Active suppressions from user doc
    const nowIso = new Date().toISOString();
    const activeSuppressed = (user?.suppressed_topics ?? []).filter(
      s => s.expires_at === null || s.expires_at === undefined || s.expires_at > nowIso
    );

    const handleUnsuppress = async (category) => {
      try {
        await api.delete(`/users/suppress/${encodeURIComponent(category)}`);
        toast.success('Category restored to your feed');
        await refreshUser();
      } catch {
        toast.error('Failed to restore category');
      }
    };

    return (
      <div className="space-y-6">
        <div className="p-6 rounded-2xl bg-[var(--theme-card)] border border-[var(--theme-border)] shadow-card">
          <div className="flex items-center gap-3 mb-2">
            <Sliders className="w-5 h-5 text-[#C9956C]" />
            <h3 className="font-bold text-[var(--theme-text-primary)]">Feed Preferences</h3>
          </div>
          <p className="text-sm text-[var(--theme-text-secondary)] mb-6">
            Choose the categories you're most interested in. Your "For You" feed will prioritise
            these when you don't have active Want Ads.
          </p>
          <CategoryPreferencePicker
            selectedCategories={feedPrefCategories}
            selectedSubcategories={feedPrefSubcategories}
            onChange={({ categories, subcategories }) => {
              setFeedPrefCategories(categories);
              setFeedPrefSubcategories(subcategories);
            }}
            onSubmit={handleSaveFeedPrefs}
            submitLabel={savingFeedPrefs ? 'Saving…' : 'Save Preferences'}
          />
        </div>

        {activeSuppressed.length > 0 && (
          <div className="p-6 rounded-2xl bg-[var(--theme-card)] border border-[var(--theme-border)] shadow-card">
            <div className="flex items-center gap-3 mb-2">
              <X className="w-5 h-5 text-[var(--theme-text-muted)]" />
              <h3 className="font-bold text-[var(--theme-text-primary)]">Hidden Topics</h3>
            </div>
            <p className="text-sm text-[var(--theme-text-secondary)] mb-4">
              These categories are currently hidden from your For You feed. Click ✕ to restore them.
            </p>
            <div className="flex flex-wrap gap-2">
              {activeSuppressed.map(s => {
                const expiryText = s.expires_at
                  ? `until ${new Date(s.expires_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}`
                  : 'indefinitely';
                return (
                  <div
                    key={s.category}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[var(--theme-hover)] border border-[var(--theme-border)] text-sm text-[var(--theme-text-primary)]"
                  >
                    <span>{s.label || s.category}</span>
                    <span className="text-xs text-[var(--theme-text-muted)]">({expiryText})</span>
                    <button
                      onClick={() => handleUnsuppress(s.category)}
                      className="ml-1 w-4 h-4 flex items-center justify-center rounded-full hover:bg-red-100 hover:text-red-600 transition-colors text-[var(--theme-text-muted)]"
                      aria-label={`Restore ${s.label || s.category}`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Render content based on active section
  const renderContent = () => {
    switch (activeSection) {
      case 'profile':
        return renderProfileSection();
      case 'notifications':
        return renderNotificationsSection();
      case 'feed-preferences':
        return renderFeedPreferencesSection();
      case 'premium':
        return renderPremiumSection();
      case 'payment-methods':
        return <PaymentMethodsSection />;
      case 'account':
        return renderAccountSection();
      default:
        return renderProfileSection();
    }
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 py-6">
        <h1 className="text-2xl font-bold text-[var(--theme-text-primary)] mb-6">Settings</h1>
        
        <div className="flex gap-6">
          {/* Sidebar Menu */}
          <div className="w-64 flex-shrink-0">
            <div className="sticky top-24 p-2 rounded-2xl bg-[var(--theme-card)] border border-[var(--theme-border)] shadow-card">
              <nav className="space-y-1">
                {MENU_ITEMS.map((item) => {
                  const Icon = item.icon;
                  const isActive = activeSection === item.id;
                  
                  return (
                    <button
                      key={item.id}
                      onClick={() => handleMenuClick(item)}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-colors ${
                        isActive 
                          ? 'bg-[#1E3A5F] text-white' 
                          : item.danger
                          ? 'text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20'
                          : 'text-[var(--theme-text-primary)] hover:bg-[var(--theme-hover)]'
                      }`}
                      data-testid={`menu-${item.id}`}
                    >
                      <Icon className={`w-5 h-5 ${isActive ? 'text-white' : item.danger ? 'text-red-500' : 'text-[var(--theme-text-secondary)]'}`} />
                      <span className="font-medium">{item.label}</span>
                    </button>
                  );
                })}
              </nav>
            </div>
          </div>

          {/* Content Area */}
          <div className="flex-1 min-w-0">
            {renderContent()}
          </div>
        </div>
      </div>
    </Layout>
  );
}
